import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload } from "lucide-react";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { TextInput, Select, Textarea } from "@mantine/core";
import { imgNbaLogo } from "../../../../assets";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../lib/usePerformRequest";
import { ButtonLoader } from "../../../../components/Loader";

export default function KYCStep2() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      propertyOwnership: "",
      propertyType: "",
      propertyAddress: "",
      propertyValue: "",
      coordinates: "",
      propertyDescription: "",
    },
    validate: {
      propertyOwnership: (value: string) => {
        if (!value.trim()) {
          return "Property ownership is required";
        }
        return null;
      },
      propertyType: (value: string) => {
        if (!value.trim()) {
          return "Property type is required";
        }
        return null;
      },
      propertyAddress: (value: string) => {
        if (!value.trim()) {
          return "Property address is required";
        }
        return null;
      },
      propertyValue: (value: string) => {
        if (!value.trim()) {
          return "Property value is required";
        }
        const numValue = parseFloat(value.replace(/[^\d.]/g, ""));
        if (isNaN(numValue) || numValue <= 0) {
          return "Please enter a valid property value";
        }
        return null;
      },
      coordinates: (value: string) => {
        if (!value.trim()) {
          return "Coordinates or survey plan number is required";
        }
        return null;
      },
      propertyDescription: (value: string) => {
        if (!value.trim()) {
          return "Property description is required";
        }
        return null;
      },
    },
  });

  const propertyOwnershipOptions = [
    "Freehold",
    "Leasehold",
    "Customary",
    "Statutory Right of Occupancy",
  ];

  const propertyTypeOptions = [
    "Residential",
    "Commercial",
    "Industrial",
    "Mixed Use",
    "Land",
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleNext = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("property_ownership", values.propertyOwnership);
      formData.append("property_type", values.propertyType);
      formData.append("property_address", values.propertyAddress);
      formData.append(
        "property_value",
        values.propertyValue.replace(/[^\d.]/g, "")
      );
      formData.append("coordinates", values.coordinates);
      formData.append("property_description", values.propertyDescription);
      if (uploadedFile) {
        formData.append("document", uploadedFile);
      }

      const endpoint = Endpoints.SubmitKYCStep2();
      const response = await PerformRequest<{ success: boolean }>({
        method: endpoint.method,
        route: endpoint.route,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        notifications.show({
          title: "Success",
          message: "Property information saved successfully",
          color: "green",
        });
        navigate("/d/kyc/step3");
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to save property information. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to save property information. Please try again.",
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="kyc-page">
      <div className="kyc-header">
        <div className="kyc-logo-section">
          <img className="kyc-logo-img" src={imgNbaLogo} alt="NBA Logo" />
          <div className="kyc-logo-text">
            <div className="kyc-logo-name">NIGERIAN BAR ASSOCIATION</div>
            <div className="kyc-logo-sub">REMUNERATION PORTAL</div>
          </div>
        </div>
      </div>

      <div className="kyc-progress">
        <button
          className="kyc-back-button"
          type="button"
          onClick={() => navigate("/d/kyc/step1")}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="kyc-progress-content">
          <span className="kyc-progress-text">Step 2 of 7</span>
          <div className="kyc-progress-bar">
            <div className="kyc-progress-fill" style={{ width: "28.6%" }} />
          </div>
        </div>
      </div>

      <div className="kyc-container">
        <div className="kyc-content">
          <div className="kyc-section-header">
            <h1 className="kyc-title">Property Address</h1>
            <p className="kyc-subtitle">
              Enter the property information and address details
            </p>
          </div>

          <div className="kyc-info-box">
            <p>Fields marked with * are required for processing</p>
          </div>

          <form className="kyc-form" onSubmit={form.onSubmit(handleNext)}>
            <Select
              label="Property Ownership *"
              placeholder="Select property Ownership"
              data={propertyOwnershipOptions}
              disabled={isSubmitting}
              error={form.errors.propertyOwnership}
              {...form.getInputProps("propertyOwnership")}
            />

            <Select
              label="Property Type *"
              placeholder="Select property type"
              data={propertyTypeOptions}
              disabled={isSubmitting}
              error={form.errors.propertyType}
              {...form.getInputProps("propertyType")}
            />

            <TextInput
              label="Property Address *"
              placeholder="Enter street address, city, state"
              disabled={isSubmitting}
              error={form.errors.propertyAddress}
              {...form.getInputProps("propertyAddress")}
            />

            <div className="kyc-upload-section">
              <label className="dashboard-form__label">Upload document</label>
              <div className="kyc-upload-wrapper">
                <label className="kyc-upload-button" style={{ cursor: "pointer" }}>
                  <Upload size={18} />
                  <span>{uploadedFile ? uploadedFile.name : "Select document"}</span>
                  <input
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={isSubmitting}
                  />
                </label>
              </div>
            </div>

            <div className="kyc-section-header">
              <h2 className="kyc-title">Property Details</h2>
              <p className="kyc-subtitle">
                Provide specific details about the property
              </p>
            </div>

            <div className="dashboard-form__grid">
              <TextInput
                label="Property Value *"
                placeholder="Enter property value in Naira"
                disabled={isSubmitting}
                error={form.errors.propertyValue}
                description="Enter the total property value in Nigerian Naira (₦)"
                {...form.getInputProps("propertyValue")}
              />

              <TextInput
                label="Coordinates or Survey Plan No. *"
                placeholder="e.g 6.44557* N, 3.4322* E or Survey No. PL1348"
                disabled={isSubmitting}
                error={form.errors.coordinates}
                description="Enter Gps coordinates or survey plan number for property verification"
                {...form.getInputProps("coordinates")}
              />
            </div>

            <Textarea
              label="Property Description *"
              placeholder="e.g 4 bedroom duplex, 3 bathroom, BQ, swimming pool, gated estate"
              rows={4}
              disabled={isSubmitting}
              error={form.errors.propertyDescription}
              {...form.getInputProps("propertyDescription")}
            />

            <button
              className="dashboard-button dashboard-button--primary dashboard-button--large flex items-center justify-center gap-2"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <ButtonLoader color="white" />
                  <span>Saving...</span>
                </>
              ) : (
                "Next"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

